import { useCallback, useEffect, useRef, useState } from "react";
import { USER_PERMISSIONS } from "config/constants";
import { useAppApolloClient } from "hooks/apollo/apollo";
import { useGetUserPermissions } from "hooks/user/user";
import { hasPermission } from "utils/helperFuncs";
import * as api from "./apiAdapters";

// Gọi adapter GraphQL qua Apollo client của app; HTTP 401 do errorLink của client đăng xuất như REST cũ.
function useAdminCall() {
  const { apolloClient } = useAppApolloClient();
  // useAppApolloClient tạo lại client khi logout handler đổi identity; giữ qua ref để không refetch vô hạn.
  const clientRef = useRef(apolloClient);
  clientRef.current = apolloClient;
  return useCallback(async (adapter, args) => {
    try {
      return await adapter(...args, clientRef.current);
    } catch (error) {
      throw api.parseApiError(error);
    }
  }, []);
}

function useResource(adapter, args, enabled = true) {
  const call = useAdminCall();
  const key = JSON.stringify(args);
  const [state, setState] = useState({ key, data: null, loading: enabled, error: null });
  const sequence = useRef(0);
  const refetch = useCallback(async () => {
    const request = ++sequence.current;
    if (!enabled) {
      setState({ key, data: null, loading: false, error: null });
      return;
    }
    setState((value) => ({ ...value, loading: true, error: null }));
    try {
      const data = await call(adapter, JSON.parse(key));
      if (request === sequence.current) setState({ key, data, loading: false, error: null });
      return data;
    } catch (error) {
      if (request === sequence.current) setState((value) => ({ ...value, loading: false, error }));
    }
  }, [adapter, key, enabled, call]);
  useEffect(() => {
    setState({ key, data: null, loading: enabled, error: null });
    refetch();
    return () => {
      sequence.current += 1;
    };
  }, [refetch, enabled, key]);
  const setData = useCallback(
    (data) => setState((value) => ({ ...value, data: typeof data === "function" ? data(value.data) : data })),
    []
  );
  return {
    data: state.key === key ? state.data : null,
    error: state.key === key ? state.error : null,
    loading: state.key === key ? state.loading : enabled,
    refetch,
    setData,
  };
}
export const useProjectTypes = (params) => useResource(api.listProjectTypes, [params]);
export const useProjectType = (id) => useResource(api.getProjectType, [id], Boolean(id));
export const useProjectQuestions = (id) => useResource(api.listProjectQuestions, [id], Boolean(id));
export const useProjectPackages = (id) => useResource(api.listProjectPackages, [id], Boolean(id));
export const useProjectPackage = (id) => useResource(api.getProjectPackage, [id], Boolean(id));
export function useCatalogProducts(keyword) {
  const trimmed = keyword.trim();
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(trimmed), 300);
    return () => clearTimeout(timer);
  }, [trimmed]);
  return useResource(api.listCatalogProducts, [{ keyword: debounced }], trimmed.length >= 2 && trimmed === debounced);
}
export function useProjectTypeMutation(adapter) {
  const call = useAdminCall();
  const [state, setState] = useState({ data: null, loading: false, error: null });
  const pending = useRef(null);
  const mutate = (...args) => {
    if (pending.current) return pending.current;
    const execute = async () => {
      setState({ data: null, loading: true, error: null });
      try {
        const data = await call(adapter, args);
        setState({ data, loading: false, error: null });
        return data;
      } catch (error) {
        setState({ data: null, loading: false, error });
        throw error;
      } finally {
        pending.current = null;
      }
    };
    pending.current = execute();
    return pending.current;
  };
  return { ...state, mutate, refetch: mutate };
}
export const useCreateProjectType = () => useProjectTypeMutation(api.createProjectType);
export const useUpdateProjectType = () => useProjectTypeMutation(api.updateProjectType);
export const useSetProjectTypeVisibility = () => useProjectTypeMutation(api.setProjectTypeVisibility);
export const useCreateProjectQuestion = () => useProjectTypeMutation(api.createProjectQuestion);
export const useUpdateProjectQuestion = () => useProjectTypeMutation(api.updateProjectQuestion);
export const useDeleteProjectQuestion = () => useProjectTypeMutation(api.deleteProjectQuestion);
export const useReorderProjectQuestions = () => useProjectTypeMutation(api.reorderProjectQuestions);
export const useCreateProjectPackage = () => useProjectTypeMutation(api.createProjectPackage);
export const useUpdateProjectPackage = () => useProjectTypeMutation(api.updateProjectPackage);
// Gọi C11 theo yêu cầu (không tự tải khi mount), vd tải lại sau lỗi PACKAGE_TIER_ALREADY_VISIBLE ở W4.
export const useReloadProjectPackages = () => useProjectTypeMutation(api.listProjectPackages);
export const useDeleteProjectPackage = () => useProjectTypeMutation(api.deleteProjectPackage);
export const useSetProjectPackageVisibility = () => useProjectTypeMutation(api.setProjectPackageVisibility);
export function useProjectTypePermissions() {
  const { data = [], loading } = useGetUserPermissions();
  return { canConfigure: !loading && hasPermission(data, [USER_PERMISSIONS.PROJECT_CONFIG]), loading };
}
